import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { type PartialCartItem } from '@cart-services/types'
import { limitedFrozenSku } from '@constants'
import { sendFastpassAddToCartEvent } from '@dni-analysis'
import { generateItemVariant } from '@dni-analysis/events/utils'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type DeliveryClass } from '@hooks-api/types/dataModel/shipping'
import { DIALOG_CHECK_DELIVERY_CLASS } from '../../merchant/product-catalog/components/DialogDeliveryClass/DialogDeliveryClass'
import { DIALOG_CHECK_WEIGHT_ATTRIBUTE } from '../../merchant/product-catalog/components/DialogWeightAttribute/DialogWeightAttribute'
import { useProductCatalog } from './use-product-catalog'

export interface ItemWantToAdd extends PartialCartItem {
  delivery_class: DeliveryClass | undefined
  sku: string
  variation_id: number
  name: string
  sale_price: number
}

export const useHandleProductAddToCart = () => {
  const { campaignId, projectCode, campaignCode, currency, platform } = useProductCatalog()
  const { items: cartItems, upsertCartItem } = useFpB2bCartService(campaignId, platform)
  const { openDialog: openDialogDeliveryClass } = useDialog(DIALOG_CHECK_DELIVERY_CLASS)
  const { openDialog: openDialogWeightAttribute } = useDialog(DIALOG_CHECK_WEIGHT_ATTRIBUTE)

  const handleProductAddToCart = useCallbackEvent((itemWantToAdd: ItemWantToAdd) => {
    const itemWantToAddDeliveryClass = itemWantToAdd.delivery_class
    const hasFrozenDelivery = cartItems.some(item => item.delivery_class === 'frozen')
    const hasNormalDelivery = cartItems.some(item => item.delivery_class === 'normal')
    if ((itemWantToAddDeliveryClass === 'frozen' && hasNormalDelivery) || (itemWantToAddDeliveryClass === 'normal' && hasFrozenDelivery)) {
      openDialogDeliveryClass()
      return
    }

    const currentSkuIsNotForbidden = !limitedFrozenSku.includes(itemWantToAdd.sku)
    const hasLimitedFrozenSkuInCart = cartItems.some(item => limitedFrozenSku.includes(item.sku))
    if (hasLimitedFrozenSkuInCart && currentSkuIsNotForbidden) {
      openDialogWeightAttribute()
      return
    }

    const {
      prevState: prevCartItems,
    } = upsertCartItem(itemWantToAdd)
    const prevCartItem = prevCartItems?.find(item => item.cartItemId === itemWantToAdd.cartItemId)
    const wantToAddQuantity = itemWantToAdd.quantity ?? 0
    const hasAddToCart = !prevCartItem || wantToAddQuantity > prevCartItem.quantity
    if (hasAddToCart) {
      sendFastpassAddToCartEvent({
        campaign_code: campaignCode,
        value: itemWantToAdd.sale_price,
        currency,
        items: [{
          item_name: itemWantToAdd.name,
          item_id: String(itemWantToAdd.cartItemId),
          item_variant: generateItemVariant({
            productName: itemWantToAdd.name,
            attributes: itemWantToAdd.attributes,
          }),
          item_brand: projectCode,
          quantity: wantToAddQuantity,
          price: itemWantToAdd.sale_price,
          currency,
        }],
      })
    }
  })

  return { handleProductAddToCart }
}
